import React, { useEffect, useState } from 'react';
import { usersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function ProfilePage() {
    const { user, updateUser } = useAuth();
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState({
        current: false,
        next: false,
        confirm: false,
    });
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    const passwordStrength = (() => {
        const value = passwordForm.newPassword || '';
        let score = 0;
        if (value.length >= 8) score += 1;
        if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
        if (/\d/.test(value)) score += 1;
        if (/[^A-Za-z0-9]/.test(value)) score += 1;
        if (value.length >= 12) score += 1;

        if (!value) return { label: 'Not set', color: '#9ca3af', width: '0%' };
        if (score <= 1) return { label: 'Weak', color: '#ef4444', width: '25%' };
        if (score <= 3) return { label: 'Medium', color: '#f59e0b', width: '60%' };
        if (score === 4) return { label: 'Strong', color: '#10b981', width: '85%' };
        return { label: 'Very strong', color: '#059669', width: '100%' };
    })();

    useEffect(() => {
        usersApi.me().then((res) => {
            if (!res.success || !res.data) return;
            setForm((prev) => ({
                ...prev,
                first_name: res.data.first_name || '',
                last_name: res.data.last_name || '',
                email: res.data.email || '',
                phone: res.data.phone || '',
            }));
        });
    }, []);

    const saveProfile = async (event) => {
        event.preventDefault();
        if (!user?.id) return;
        setSaving(true);
        const payload = {
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            phone: form.phone,
        };

        const response = await usersApi.update(user.id, payload);
        setSaving(false);

        if (!response.success) {
            window.alert(response.error || 'Failed to save profile');
            return;
        }

        window.alert('Profile updated successfully');
    };

    const changePassword = async (event) => {
        event.preventDefault();
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            window.alert('Please complete all password fields.');
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            window.alert('New password and confirmation do not match.');
            return;
        }

        setChangingPassword(true);
        const response = await usersApi.changePassword(passwordForm);
        setChangingPassword(false);

        if (!response.success) {
            window.alert(response.error || 'Failed to change password');
            return;
        }

        window.alert('Password changed successfully');
        if (user?.mustChangePassword) {
            updateUser({ ...user, mustChangePassword: false });
        }
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Profile</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Profile</p>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>My Account</h3>
                    <form className="stock-form-grid" onSubmit={saveProfile}>
                        <label>
                            <span>First Name</span>
                            <input value={form.first_name} onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Last Name</span>
                            <input value={form.last_name} onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Email</span>
                            <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Profile'}</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Change Password</h3>
                    <p className="muted-sm" style={{ marginTop: 0 }}>
                        Update the current store admin password without changing profile details.
                    </p>
                    <form className="stock-form-grid" onSubmit={changePassword}>
                        <label>
                            <span>Current Password</span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type={showPassword.current ? 'text' : 'password'}
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    className="btn btn-outline btn-sm"
                                    type="button"
                                    onClick={() => setShowPassword((prev) => ({ ...prev, current: !prev.current }))}
                                >
                                    {showPassword.current ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </label>
                        <label>
                            <span>New Password</span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type={showPassword.next ? 'text' : 'password'}
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    className="btn btn-outline btn-sm"
                                    type="button"
                                    onClick={() => setShowPassword((prev) => ({ ...prev, next: !prev.next }))}
                                >
                                    {showPassword.next ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <div style={{ height: 8, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                                    <div style={{ width: passwordStrength.width, height: '100%', background: passwordStrength.color, transition: 'width 180ms ease' }} />
                                </div>
                                <small style={{ color: passwordStrength.color }}>Strength: {passwordStrength.label}</small>
                            </div>
                        </label>
                        <label>
                            <span>Confirm New Password</span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type={showPassword.confirm ? 'text' : 'password'}
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    className="btn btn-outline btn-sm"
                                    type="button"
                                    onClick={() => setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))}
                                >
                                    {showPassword.confirm ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn btn-outline" type="submit" disabled={changingPassword}>
                                {changingPassword ? 'Updating...' : 'Change Password'}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
}
